<template>
  <base-modal
    v-if="modelValue !== null"
    size="lg"
    hide-footer
    hide-header
    @close="$emit('cancel')"
  >
    <div v-if="recordUpdate" class="col-sm-12 editor-form">
      <div>
        <h4 class="editor-header">
          {{ $t('material_station.scale') }}
        </h4>
        <span class="pl-3 pb-2">{{ recordUpdate.scale.name + ' (' + recordUpdate.scale_id + ')' }}</span>
        <div class="row nopads">
          <div class="col-sm-5">
            <span class="span-title">{{ $t('delivery_note.contract') }}</span>
              <select
                  v-model="contract"
                  class="form-select form-select-sm"
                  :disabled="!!loading"
                  @change="selectContract"
              >
                  <option :value="null">
                      {{ $t('delivery_note.form.select_contract') }}
                  </option>
                  <option
                      v-for="contract in contracts"
                      :key="contract.id"
                      :value="contract.id"
                  >
                      {{ contract.name }}
                  </option>
              </select>
          </div>
          <div class="col-sm-5">
              <contract-order-multiselect
                  ref="orderFilter"
                  :contract-ids="contract ?  [contract] : []"
                  :single-mode="true"
                  @orders-selected="setOrder"
              />
          </div>
        </div>
        <div class="row">
          <div class="col-sm-5">
            <span class="span-title">{{ $t('delivery_note.vehicle') }}</span>
              <input
                  v-model="recordUpdate.vehicle_license_plate"
                  type="text"
                  placeholder="ABC-123"
                  class="form-control form-control-sm"
                  :disabled="!recordUpdate || !!loading"
              />
          </div>
          <div class="col-sm-5">
            <span class="span-title">{{ $t('delivery_note.material') }}</span>
              <select
                  v-model="recordUpdate.material_id"
                  class="form-select form-select-sm"
                  :disabled="!!loading"
              >
                  <option :value="null">
                      {{ $t('delivery_note.form.select_material') }}
                  </option>
                  <option
                      v-for="material in recordUpdate.scale.station.station_materials"
                      :key="material.id"
                      :value="material.id"
                  >
                      {{ material.material.name + ' (' + material.type_test_code + ')' }}
                  </option>
              </select>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-5">
            <span class="span-title">{{ $t('material_station.truck_net_weight') }}</span>
              <input
                  v-model="recordUpdate.truck_net_weight"
                  type="number"
                  step="0.01"
                  class="form-control form-control-sm"
                  :disabled="!!loading"
              />
          </div>
          <div class="col-sm-5">
            <span class="span-title">{{ $t('material_station.trainer_net_weight') }}</span>
              <input
                  v-model="recordUpdate.trailer_net_weight"
                  type="number"
                  step="0.01"
                  class="form-control form-control-sm"
                  :disabled="!!loading"
              />
          </div>
        </div>
      </div>
      <div class="col-12 d-flex justify-content-end">
        <button
          :disabled="!!loading"
          class="btn btn-secondary form-button"
          @click.stop="$emit('cancel')"
        >
          <span>{{ $t('common.cancel') }}</span>
        </button>
        <button
          :disabled="!!loading || !canSubmit"
          class="btn btn-success form-button"
          @click.stop="submitRecord"
        >
          <span>{{ $t('common.save') }}</span>
        </button>
      </div>
    </div>
    <div
      v-if="!!loading"
      class="spinner"
    />
  </base-modal>
</template>

<script>
import BaseModal from "@/components/BaseModal";
import {restApi} from "@/components/mixins/RestApiMixin";
import {timeUtils} from "@/components/mixins/TimeUtils";
import {orderMixin} from "@/components/mixins/OrderMixin";
import {inject} from "vue";
import ContractOrderMultiselect from "../../order/ContractOrderMultiselect.vue";

export default {
    name: "ExternalScaleRecordModal",
    components: {ContractOrderMultiselect, BaseModal},
    mixins: [restApi, timeUtils, orderMixin],
    props: {
        modelValue: {
            type: Object
        }
    },
    data() {
        return {
            loading: 0,
            recordUpdate: null,
            contracts: [],
            contract: null,
            order: null
        }
    },
    computed: {
        canSubmit() {
            return this.recordUpdate && this.recordUpdate.vehicle_license_plate && this.recordUpdate.order_id && this.recordUpdate.material_id
        }
    },
    watch: {
        order: function () {
            this.recordUpdate.order_id = this.order ? this.order.id : -1
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert')
        return { showAlert }
    },
    created() {
        this.fetchValidatedRecord()
    },
    methods: {
        setOrder(order) {
            this.order = order
        },
        fetchValidatedRecord() {
            this.loading++
            this.restFetch(this.scaleRecordUrl + '/' + this.modelValue.id, response => {
                this.recordUpdate = response.data
                this.order = this.recordUpdate.order
                this.loading--
                this.fetchContracts()
            }, () => {
               this.showAlert(this.$t('material_station.error_editing_record'))
                this.$emit('cancel')
            })
        },
        fetchContracts: function () {
            let params = {}
            params.contractor = this.recordUpdate.scale.station.company.id
            params.startDate = this.getStartOfTheDay(new Date())
            params.endDate = this.getEndOfTheDay(new Date())
            this.loading++
            this.restFetchParams(this.contractOpenUrl, params, response => {
                this.loading--
                this.contracts = response.data
            }, () => {
                this.loading--
            })
        },
        selectContract: function () {
            this.order = null
        },
        submitRecord: function () {
            this.loading++
            let record = {}
            record.id = this.recordUpdate.id
            record.scale_id = this.recordUpdate.scale_id
            record.order_id = this.recordUpdate.order_id
            record.vehicle_license_plate = this.recordUpdate.vehicle_license_plate
            record.material_id = this.recordUpdate.material_id
            record.trailer_net_weight = this.recordUpdate.trailer_net_weight
            record.truck_net_weight = this.recordUpdate.truck_net_weight
            this.restUpdate(this.scaleRecordUrl, record, this.submitRecordSuccess, this.submitRecordFail)
        },
        submitRecordSuccess: function () {
            this.$emit('closeAndUpdate')
            this.loading--
        },
        submitRecordFail: function () {
            this.loading--
            this.showAlert(this.$t('common.alert_update_failed'))
        }
    }
}
</script>
